import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentResult() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState("checking");
  const ran = useRef(false);

  const isCancel = location.pathname.includes("cancel");
  const provider = params.get("provider") || "stripe";
  const sessionId = params.get("session_id") || params.get("reference") || params.get("trxref") || params.get("token");

  useEffect(() => {
    if (isCancel) { setState("cancelled"); return; }
    if (ran.current) return;
    ran.current = true;
    if (!sessionId) { setState("failed"); return; }

    const check = async () => {
      try {
        if (provider === "paystack") {
          const res = await fetch(`${API}/payments/paystack/verify/${sessionId}`, { credentials: "include" });
          const data = await res.json();
          setState(data.status === "paid" ? "paid" : "failed");
        } else if (provider === "paypal") {
          const res = await fetch(`${API}/payments/paypal/capture/${sessionId}`, { method: "POST", credentials: "include" });
          const data = await res.json();
          setState(data.status === "paid" ? "paid" : "failed");
        } else if (provider === "razorpay") {
          const res = await fetch(`${API}/payments/status/${sessionId}`);
          const data = await res.json();
          setState(data.payment_status === "paid" ? "paid" : "failed");
        } else {
          for (let i = 0; i < 10; i++) {
            const res = await fetch(`${API}/payments/status/${sessionId}`);
            const data = await res.json();
            if (data.payment_status === "paid") { setState("paid"); return; }
            await new Promise((r) => setTimeout(r, 2000));
          }
          setState("failed");
        }
      } catch {
        setState("failed");
      }
    };
    check();
  }, [isCancel, provider, sessionId]);

  const content = {
    checking: { icon: <Loader2 className="h-14 w-14 animate-spin text-amber-500" />, title: "Confirming payment", body: "Hold on — we're verifying with the payment provider." },
    paid: { icon: <CheckCircle2 className="h-14 w-14 text-emerald-400" />, title: "Founding Pass active", body: "Welcome to the founding 500. Your badge and benefits are locked to your account." },
    cancelled: { icon: <XCircle className="h-14 w-14 text-slate-500" />, title: "Checkout cancelled", body: "No charge was made. The pass will be here when you're ready." },
    failed: { icon: <XCircle className="h-14 w-14 text-red-400" />, title: "Payment not confirmed", body: "If you were charged, it will auto-refund. Try again or use another provider." },
  }[state];

  return (
    <div data-testid="payment-result-page" className="flex min-h-screen items-center justify-center bg-[#07090e] px-4">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0f141c] p-10 text-center">
        <div className="flex justify-center">{content.icon}</div>
        <h1 data-testid="payment-result-title" className="font-display mt-6 text-3xl font-black uppercase text-slate-50">{content.title}</h1>
        <p className="mt-3 text-sm leading-relaxed text-slate-400">{content.body}</p>
        <button
          data-testid="payment-result-home-btn"
          onClick={() => navigate("/")}
          className="mt-8 h-12 w-full rounded-md bg-amber-500 font-display text-sm font-bold uppercase text-[#07090e] transition-colors hover:bg-amber-400"
        >
          Back to GuardGig28
        </button>
      </div>
    </div>
  );
}
